import * as React from 'react';
import {
  useHtmlEmbedOpenCanvasRewriter,
  useIsOpenCanvasActive,
} from '../contexts/OpenCanvasContext';
import {prepareCustomCodeForExecutionInCanvasApp} from '../utils/prepareCustomCodeForExecutionInCanvasApp';

type Props = {
  /**
   * HTML fragment to be inserted into the DOM.
   */
  fragment: string | undefined;
  /**
   * Whether or not this fragment is ready to be inserted into the DOM.
   */
  isReadyToBeInserted: boolean;
};

/**
 * NEVER use this component outside of the Canvas App as it can lead to XSS attacks.
 *
 * This component allow to insert fragment of HTML into the DOM just before the noscript tag.
 * If any of the scripts are in the fragment they will be inserted and executed.
 *
 * When the canvas app is rendering Open Canvas (Multi-Context / Component
 * Canvas), the inserted HTML's viewport-relative length units are rewritten
 * so they resolve against the active frame rather than the iframe viewport.
 * This signal is read from `OpenCanvasContext`; consumers don't need to
 * thread it through props.
 */
export const HtmlFragment = ({fragment, isReadyToBeInserted}: Props) => {
  const isOpenCanvasActive = useIsOpenCanvasActive();
  const rewriteHtmlEmbedForOpenCanvas = useHtmlEmbedOpenCanvasRewriter();
  const fragmentPlacementEndRef = React.useRef<null | HTMLElement>(null);
  const fragmentPlacementStartRef = React.useRef<null | HTMLElement>(null);

  React.useLayoutEffect(() => {
    if (!isReadyToBeInserted) return;

    const start = fragmentPlacementStartRef.current;
    const end = fragmentPlacementEndRef.current;

    if (!start || !end) return;

    // we need to clean up previously added fragments. This will allow, for example, page settings
    // custom code correctly be updated correctly on the page change.
    while (start.nextElementSibling && start.nextElementSibling !== end) {
      start.nextElementSibling.remove();
    }

    const prepared = prepareCustomCodeForExecutionInCanvasApp(fragment || '');

    const insertFragment = (html: string) => {
      const documentFragment = document
        .createRange()
        .createContextualFragment(html);
      end.parentNode?.insertBefore(documentFragment, end);
    };

    if (!isOpenCanvasActive) {
      insertFragment(prepared);
      return;
    }

    insertFragment(rewriteHtmlEmbedForOpenCanvas(prepared));
  }, [
    fragment,
    isReadyToBeInserted,
    isOpenCanvasActive,
    rewriteHtmlEmbedForOpenCanvas,
  ]);

  // we are using `noscript` tag as it is one of not so many tags that is allowed both inside the `head` and `body`
  // and it doesn't change any user observed behavior. Similar to how we use it in the Script component
  return (
    <>
      <noscript ref={fragmentPlacementStartRef} />
      <noscript ref={fragmentPlacementEndRef} />
    </>
  );
};
